import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { AuditEntityTypeEnum, CompanyLocationBasicInfoInDTO, CompanyLocationsDetailOutDTO, FeaturesEnum, PermissionTypesEnum, VisibilityEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import Badge from '@components/ringil3/Badges/Badge.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import InputChipsTag from '@components/RingilComponents/tags/InputChipsTag.tsx';
import RichNoteTag from '@components/RingilComponents/tags/RichNoteTag.tsx';
import LocationAddressTag from '@components/ringil3/Features/Location/Tags/LocationAddressTag.tsx';
import LocationBooleanTag from '@components/ringil3/Features/Location/Tags/LocationBooleanTag.tsx';
import LocationCoordinatesTag from '@components/ringil3/Features/Location/Tags/LocationCoordinatesTag.tsx';
import { exceptionsInWindow } from '@app/pages/customer/LocalityDetail/sections/exceptionsWindow.ts';
import { CompanyLocationExceptionPreviewOutDTO } from '@api/ringil-api.v2';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import SvgTrashBin from '@icons/delete-trash.svg?react';
import SvgRestore from '@icons/reset-filled.svg?react';
import SvgArrowsRepeat from '@icons/arrows-repeat.svg?react';
import SvgMapPin from '@icons/map-pin.svg?react';
import SvgNumber from '@icons/number.svg?react';
import SvgBuilding from '@icons/building.svg?react';
import SvgCalendar from '@components/ringil3/icons/Calendar.tsx';
import SvgUser from '@icons/user-filled.svg?react';
import SvgTabAttachments from '@components/ringil3/icons/TabAttachments.tsx';
import SvgYellowExclamation from '@components/ringil3/icons/YellowExclamation.tsx';
import SvgInfo from '@icons/info.svg?react';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import OpeningHoursSection from '@app/pages/customer/LocalityDetail/sections/OpeningHoursSection.tsx';
import ExceptionsSection from '@app/pages/customer/LocalityDetail/sections/ExceptionsSection.tsx';
import RampsSection from '@app/pages/customer/LocalityDetail/sections/RampsSection.tsx';
import CarriersApiSection from '@app/pages/customer/LocalityDetail/sections/CarriersApiSection.tsx';
import ContactsSection from '@app/pages/customer/LocalityDetail/sections/ContactsSection.tsx';
import VisibilitySection from '@app/pages/customer/LocalityDetail/sections/VisibilitySection.tsx';
import CmrSection from '@app/pages/customer/LocalityDetail/sections/CmrSection.tsx';
import SupersedeLocationDialog from '@app/pages/customer/LocalityList/dialogs/SupersedeLocationDialog.tsx';

interface ComponentProps {
  localityId: string;
}

const LocalityDetail: React.FC<ComponentProps> = ({ localityId }) => {
  const { tr } = useTranslationLgs();
  const { getCompanyLocationById, updateLocationBasicInfo, deleteCompanyLocation, activateCompanyLocation, getExceptionsForCompanyLocation } = useContext(ApiContext);
  const { hasFeature } = useContext(AuthenticatedUserContext);
  const navigation = useNavigation();

  const [data, setData] = useState<CompanyLocationsDetailOutDTO>();
  const [exceptions, setExceptions] = useState<CompanyLocationExceptionPreviewOutDTO[]>();
  const [activeTab, setActiveTab] = useState(0);
  const [editedName, setEditedName] = useState('');
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [restoreDialogVisible, setRestoreDialogVisible] = useState(false);
  const [supersedeDialogVisible, setSupersedeDialogVisible] = useState(false);
  const nameOverlayRef = useRef<OverlayPanel>(null);
  const auditLogRef = useRef<EntityAuditLogHandle>(null);

  const cmrSigningEnabled = hasFeature([FeaturesEnum.SIGNING_CMR]);
  // Audit/history is gated on its own dedicated read permission — the 2.0 audit endpoint 403s without it.
  const showAudit = useSecuredFlag('location-detail-audit', {
    kind: 'visibility',
    description: 'Location change history / audit log',
    permissions: [PermissionTypesEnum.PERMISSION_LOCATION_DETAIL_AUDITLOG_READ],
  });

  // Feature-gated only, like the neighbouring Delete action which carries no permission gate either.
  const supersedeFlag = useSecuredFlag('location-detail-supersede', {
    kind: 'action',
    description: 'Replace a location by another one',
    features: [FeaturesEnum.SUPERSEDE_LOCATION],
  });

  const load = () => {
    getCompanyLocationById(localityId, d => {
      setData(d);
      getExceptionsForCompanyLocation(localityId, setExceptions);
      auditLogRef.current?.reload();
    });
  };

  useEffect(() => {
    load();
  }, [localityId]);

  if (!data) return null;

  // A deleted location (a superseded one is deleted too) cannot be edited at all — Restore is the only
  // action offered. This is a UI rule, not a security boundary: the backend still accepts the writes.
  const readOnly = data.isDeleted;

  const saveLocation = (overrides: Partial<CompanyLocationBasicInfoInDTO>) => {
    const body: CompanyLocationBasicInfoInDTO = {
      name: data.name,
      streetNr: data.streetNr,
      city: data.city,
      postalCode: data.postalCode,
      country: data.country,
      timezoneId: data.timezoneId,
      externalId: data.externalId,
      externalIds: data.externalIds,
      enableVisitor: data.enableVisitor,
      isPartnerLocation: data.isPartnerLocation,
      locationNote: data.locationNote,
      latitude: data.latitude,
      longitude: data.longitude,
      ...overrides,
    };
    updateLocationBasicInfo(localityId, body, () => load());
  };

  const visibilityLabel = {
    [VisibilityEnum.PUBLIC]: tr('LocalityDetail.visibilityAll', 'All'),
    [VisibilityEnum.PRIVATE]: tr('LocalityDetail.visibilityInternal', 'Internal'),
    [VisibilityEnum.VISIBLE_TO_COMPANIES]: tr('LocalityDetail.visibilitySelected', 'Selected'),
  }[data.acl?.visibility];

  const yes = tr('generic.yes', 'Yes');
  const no = tr('generic.no', 'No');

  const tabs: TabProps[] = [
    { label: tr('LocalityDetail.tabOpeningHoursAndRamps', 'Opening hours and ramps'), active: activeTab === 0, anchorId: 'location-opening', icon: <SvgCalendar /> },
    { label: tr('LocalityDetail.tabLocation', 'Location'), active: activeTab === 1, anchorId: 'location-carriers', icon: <SvgMapPin /> },
    { label: tr('LocalityDetail.tabContacts', 'Contacts'), active: activeTab === 2, anchorId: 'location-contacts', icon: <SvgUser /> },
    { label: tr('LocalityDetail.tabVisibility', 'Visibility'), active: activeTab === 3, anchorId: 'location-visibility' },
  ];
  if (cmrSigningEnabled) {
    tabs.push({ label: tr('LocalityDetail.tabCmr', 'CMR'), active: activeTab === 4, anchorId: 'location-cmr', icon: <SvgTabAttachments /> });
  }
  // History is a switch tab (no anchorId), so scroll-spy leaves it selected while the audit panel replaces the sections.
  const historyTabIndex = tabs.length;
  if (showAudit.granted) {
    tabs.push({ label: tr('LocalityDetail.tabHistory', 'History'), active: activeTab === historyTabIndex, icon: <SvgInfo /> });
  }

  return (
    <div className="flex flex-col gap-4">
      <BreadCrumbComponent
        items={[
          {
            label: tr('LocalityDetail.locationList', 'Location list'),
            url: navigation.createNavigationLink(navigation.urlFunctions.createLocationsList()),
          },
          { label: tr('LocalityDetail.locationDetail', 'Location detail') },
        ]}
      />

      <div>
        <Heading
          title={data.name}
          headingVariant={'h1'}
          onEdit={
            readOnly
              ? undefined
              : e => {
                  setEditedName(data.name);
                  nameOverlayRef.current?.toggle(null, e.currentTarget);
                }
          }
          buttons={[
            <SecuredArea
              key={'location-supersede'}
              id={'location-detail-supersede'}
              label={'Replace location'} description="Replace the location with a new one"
              visible={[supersedeFlag, { type: 'data', label: 'Location is not deleted', satisfied: !data.isDeleted }]}>
              <RingilButton
                mode={'table'}
                icon={<SvgArrowsRepeat />}
                tooltip={tr('LocalityList.supersede', 'Replace…')}
                onClick={() => setSupersedeDialogVisible(true)}
                dataTestId={'location-supersede'}
              />
            </SecuredArea>,
            data.isDeleted ? (
              <RingilButton
                key={'location-restore'}
                mode={'table'}
                icon={<SvgRestore />}
                tooltip={tr('LocalityDetail.restoreLocation', 'Restore location')}
                onClick={() => (data.supersededBy ? setRestoreDialogVisible(true) : activateCompanyLocation(localityId, () => load()))}
                dataTestId={'location-restore'}
              />
            ) : (
              <RingilButton key={'location-delete'} mode={'table'} icon={<SvgTrashBin />} onClick={() => setDeleteDialogVisible(true)} dataTestId={'location-delete'} />
            ),
          ]}
        />
      </div>
      <ConfirmDialog
        visible={deleteDialogVisible}
        dialogTitle={tr('LocalityDetail.deleteLocation', 'Delete location')}
        dialogMessage={tr(
          'LocalityDetail.deleteLocationMessage',
          'The location will be marked as deleted and can no longer be used. Existing reservations and expeditions are not affected.',
        )}
        onConfirm={() => deleteCompanyLocation(localityId, false, true, () => navigation.navigate(navigation.urlFunctions.createLocationsList()))}
        onCancel={() => setDeleteDialogVisible(false)}
        onAcceptButtonText={tr('LocalityDetail.deleteLocationConfirm', 'Delete location')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
      />
      {/* Restoring is the only way the UI un-deletes a location, and un-deleting always clears the supersede link. */}
      <ConfirmDialog
        visible={restoreDialogVisible}
        dialogTitle={tr('LocalityDetail.restoreLocation', 'Restore location')}
        dialogMessage={tr('LocalityDetail.restoreSupersededMessage', 'Restoring removes the link to {{name}}. New bookings will use this location again.', {
          name: data.supersededBy?.name,
        })}
        onConfirm={() =>
          activateCompanyLocation(localityId, () => {
            setRestoreDialogVisible(false);
            load();
          })
        }
        onCancel={() => setRestoreDialogVisible(false)}
        onAcceptButtonText={tr('LocalityDetail.restoreLocationConfirm', 'Restore location')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
        dataTestId={'location-restore-dialog'}
      />
      {/*
      // @ts-ignore*/}
      <OverlayPanel ref={nameOverlayRef} className={'[&_.p-overlaypanel-content]:p-0 [&:before]:!hidden [&:after]:!hidden'}>
        <div className="flex flex-col gap-4 p-6 bg-base-secondary rounded-lg">
          <InputText value={editedName} onChange={e => setEditedName(e.target.value)} data-testid={'location-name-input'} />
          <div className="flex flex-row gap-4 mt-1">
            <RingilButton
              mode={'filled'}
              size={'big'}
              label={tr('RingilSidebar.continue', 'Continue')}
              onClick={() => {
                if (editedName?.trim()) {
                  saveLocation({ name: editedName.trim() });
                  nameOverlayRef.current?.hide();
                }
              }}
              dataTestId={'location-name-save'}
            />
            <RingilButton mode={'text-only'} size={'big'} label={tr('RingilSidebar.back', 'Back')} onClick={() => nameOverlayRef.current?.hide()} />
          </div>
        </div>
      </OverlayPanel>

      <Ringil3BadgeRow>
        {/* A superseded location is a deleted one; the forwarding badge replaces the plain "Deleted" one. */}
        {data.supersededBy ? (
          <COBadge
            variant={'filled'}
            type={'custom'}
            className={'bg-complementary-warningMedium text-content-primary cursor-pointer'}
            onClick={() => navigation.navigate(navigation.urlFunctions.createLocalityDetail(data.supersededBy.id))}
            dataTestId={'location-badge-supersededBy'}>
            {tr('LocalityList.supersededBy', 'Replaced by {{name}}', { name: data.supersededBy.name })}
          </COBadge>
        ) : (
          data.isDeleted && (
            <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-error text-white'} dataTestId={'location-badge-deleted'}>
              {tr('LocalityList.deleted', 'Deleted')}
            </COBadge>
          )
        )}
        <Badge
          label={tr('LocalityDetail.exceptionsInNext30Days', 'Exceptions in next 30 days')}
          value={`${exceptionsInWindow(exceptions, data.timezoneId ?? 'Europe/Prague').length}`}
          dataTestId={'location-badge-exceptions'}
        />
        <Badge
          label={tr('LocalityDetail.concurrentEventsCount', 'Concurrent events')}
          value={`${data.concurrentlyActiveEventsCount ?? 0}`}
          dataTestId={'location-badge-concurrentEvents'}
        />
        <Badge label={tr('LocalityDetail.ramps', 'Ramps')} value={`${data.ramps?.length ?? 0}`} dataTestId={'location-badge-ramps'} />
        <Badge label={tr('LocalityDetail.allowsVisits', 'Allows visits')} value={data.enableVisitor ? yes : no} dataTestId={'location-badge-visits'} />
        <Badge label={tr('LocalityDetail.visibility', 'Visibility')} value={visibilityLabel} dataTestId={'location-badge-visibility'} />
        <Badge label={tr('LocalityDetail.locationId', 'Location ID')} value={data.id} dataTestId={'location-badge-id'} />
        <Badge label={tr('LocalityDetail.timezoneId', 'Timezone')} value={data.timezoneId} dataTestId={'location-badge-timezone-id'} />
      </Ringil3BadgeRow>

      {readOnly && (
        <COInlineMessage variant={data.supersededBy ? 'warning' : 'info'} icon={<SvgGrayInfo />} buttons={null} dataTestId={'location-deleted-message'}>
          {data.supersededBy
            ? tr('LocalityDetail.supersededMessage', 'This location was replaced by {{name}}.', { name: data.supersededBy.name })
            : tr('LocalityDetail.deletedReadOnlyMessage', 'This location is deleted and cannot be edited. Restore it to make changes.')}
        </COInlineMessage>
      )}

      <Ringil3BadgeRow>
        <LocationAddressTag
          value={{ streetNr: data.streetNr, city: data.city, postalCode: data.postalCode, country: data.country, timezoneId: data.timezoneId }}
          onChange={v => saveLocation(v)}
          icon={<SvgMapPin />}
          readOnly={readOnly}
          dataTestId={'location-tag-address'}
        />
        <LocationBooleanTag
          label={tr('LocalityDetail.visits', 'Visits')}
          value={data.enableVisitor}
          yesLabel={tr('LocalityDetail.visitsAllowed', 'Yes, allowed')}
          noLabel={tr('LocalityDetail.visitsNotAllowed', 'No')}
          switchLabel={tr('LocalityDetail.allowsVisits', 'Allows visits')}
          helpText={tr('LocalityDetail.allowsVisitsHelp', 'Allow drivers to register a visit at this location.')}
          onChange={v => saveLocation({ enableVisitor: v })}
          icon={<SvgNumber />}
          readOnly={readOnly}
          dataTestId={'location-tag-visits'}
        />
        <InputChipsTag
          label={tr('LocalityDetail.externalIds', 'External IDs')}
          value={data.externalIds ?? []}
          onChange={v => saveLocation({ externalIds: v })}
          icon={<SvgNumber />}
          readOnly={readOnly}
          dataTestId={'location-tag-externalId'}
        />
        <LocationBooleanTag
          label={tr('LocalityDetail.partnerLocation', 'Partner location')}
          value={data.isPartnerLocation}
          yesLabel={yes}
          noLabel={no}
          switchLabel={tr('LocalityDetail.partnerLocation', 'Partner location')}
          helpText={tr('LocalityDetail.partnerLocationHelp', 'Mark this as a partner-operated location.')}
          onChange={v => saveLocation({ isPartnerLocation: v })}
          icon={<SvgBuilding />}
          readOnly={readOnly}
          dataTestId={'location-tag-partnerLocation'}
        />
        <LocationCoordinatesTag
          value={{ latitude: data.latitude, longitude: data.longitude }}
          onChange={v => saveLocation({ latitude: v.latitude, longitude: v.longitude })}
          icon={<SvgMapPin />}
          readOnly={readOnly}
          dataTestId={'location-tag-coordinates'}
        />
      </Ringil3BadgeRow>

      <Ringil3BadgeRow>
        <RichNoteTag
          label={tr('generic.locationNote', 'Location note')}
          value={data.locationNote}
          fullWidth
          onChange={v => saveLocation({ locationNote: v })}
          icon={<SvgYellowExclamation />}
          readOnly={readOnly}
          dataTestId={'location-tag-locationNote'}
        />
      </Ringil3BadgeRow>

      <Tabs tabs={tabs} onTabChange={setActiveTab} />

      {activeTab !== historyTabIndex && (
        <>
          <div id={'location-opening'} className="flex flex-col gap-4 scroll-mt-[72px]">
            <OpeningHoursSection location={data} onReload={() => load()} />
            <ExceptionsSection location={data} exceptions={exceptions} onReload={() => load()} />
            <RampsSection location={data} onReload={() => load()} />
          </div>

          <div id={'location-carriers'} className="scroll-mt-[72px]">
            <CarriersApiSection location={data} onReload={() => load()} />
          </div>

          <div id={'location-contacts'} className="scroll-mt-[72px]">
            <ContactsSection location={data} onReload={() => load()} />
          </div>

          <div id={'location-visibility'} className="scroll-mt-[72px]">
            <VisibilitySection location={data} onReload={() => load()} />
          </div>

          {cmrSigningEnabled && (
            <div id={'location-cmr'} className="scroll-mt-[72px]">
              <CmrSection location={data} onReload={() => load()} />
            </div>
          )}
        </>
      )}

      <SupersedeLocationDialog
        visible={supersedeDialogVisible}
        locationId={localityId}
        locationName={data.name}
        onHide={() => setSupersedeDialogVisible(false)}
        onCompleted={() => {
          setSupersedeDialogVisible(false);
          load();
        }}
      />

      {showAudit.granted && activeTab === historyTabIndex && (
        <SecuredArea id="location-detail-audit" label="Audit log" description="History of all changes made to the location" visible={showAudit}>
          <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.LOCATION} entityId={data.id} hideHeader />
        </SecuredArea>
      )}
    </div>
  );
};

export default LocalityDetail;
